import { Exchange, Instrument, InstrumentKind, UnderlyingRef } from '@/types/generated/common/types'

/**
 * Identity of one instrument across venues. A bare symbol is ambiguous — "IBIT"
 * names an ETF on XNAS and is also the OSI root of every IBIT option on OPRA — so
 * every cross-exchange lookup in here is keyed by the (exchange, symbol) pair.
 */
export const instrumentRefKey = (exchange: Exchange, symbol: string): string =>
  `${exchange}:${symbol}`

/**
 * The instrument's cross-exchange underlying pointer, or undefined when it has none.
 *
 * `Instrument.underlying` is populated ONLY when the underlying lists on a different
 * exchange than the instrument itself (today: OPRA options on XNAS-listed ETFs).
 * Absent means "my underlying is on my own exchange, or I have none" — which is what
 * every consumer assumed before the field existed, so absence needs no handling.
 *
 * Both halves must be set for the pointer to be resolvable: `exchange` is a proto
 * enum whose zero value (`EXCHANGE_UNSPECIFIED`) is "not set", not a venue, and
 * ts-proto materialises `{ exchange: 0, symbol: '' }` for a present-but-empty
 * message. A half-filled pointer is treated as no pointer rather than as a lookup
 * that can never match.
 */
export function underlyingRefOf(
  instrument: Instrument | null | undefined
): UnderlyingRef | undefined {
  const ref = instrument?.underlying
  if (!ref) return undefined
  if (ref.exchange === Exchange.EXCHANGE_UNSPECIFIED || !ref.symbol) return undefined
  if (ref.exchange === Exchange.UNRECOGNIZED) return undefined
  return ref
}

/** (exchange, symbol) → instrument, for resolving pointers within one instrument pool. */
export function buildInstrumentIndex(instruments: Instrument[]): Map<string, Instrument> {
  const index = new Map<string, Instrument>()
  for (const instrument of instruments) {
    index.set(instrumentRefKey(instrument.exchange, instrument.symbol), instrument)
  }
  return index
}

/**
 * The instrument this one points at, when that instrument is present in `index`.
 *
 * Resolution is deliberately one hop and never transitive: the pointer only ever
 * runs derivative → underlying, and an underlying is a spot/equity row that never
 * carries a pointer of its own, so there is no chain to walk and no cycle to guard.
 *
 * The pointer carries no liveness guarantee — the named symbol may be absent from
 * the target venue's current instrument list — so a miss is an ordinary outcome,
 * not an error.
 */
export function resolveUnderlyingInstrument(
  instrument: Instrument,
  index: Map<string, Instrument>
): Instrument | undefined {
  const ref = underlyingRefOf(instrument)
  if (!ref) return undefined
  return index.get(instrumentRefKey(ref.exchange, ref.symbol))
}

/**
 * Reverse of `resolveUnderlyingInstrument`: underlying (exchange, symbol) → every
 * instrument in `pool` that points at it.
 *
 * The proto pointer only runs derivative → underlying, so there is no field to read
 * in this direction — the only way to answer "what trades against this row?" is to
 * invert the pointers of a pool that already holds the derivatives. That makes the
 * pool the limiting factor: an underlying whose derivatives live on a venue absent
 * from `pool` resolves to nothing, the same ordinary miss a dangling forward pointer
 * produces.
 *
 * Instruments with no pointer are skipped rather than keyed under themselves — a
 * same-exchange derivative is already reachable by the ordinary symbol search its
 * consumers do, and folding it in here would put two unrelated relationships behind
 * one lookup.
 */
export function buildUnderlyingBacklinks(pool: Instrument[]): Map<string, Instrument[]> {
  const backlinks = new Map<string, Instrument[]>()
  for (const instrument of pool) {
    const ref = underlyingRefOf(instrument)
    if (!ref) continue
    const key = instrumentRefKey(ref.exchange, ref.symbol)
    const existing = backlinks.get(key)
    if (existing) existing.push(instrument)
    else backlinks.set(key, [instrument])
  }
  return backlinks
}

/** The instruments in `backlinks` that name `instrument` as their underlying. */
export function derivativesOf(
  instrument: Instrument | null | undefined,
  backlinks: Map<string, Instrument[]>
): Instrument[] {
  if (!instrument) return []
  return backlinks.get(instrumentRefKey(instrument.exchange, instrument.symbol)) ?? []
}

/**
 * Pick one active, cross-exchange option venue for an underlying.
 *
 * Backlinks may contain inactive catalog rows and, as more venues are added, options
 * from multiple exchanges. An option chain must never mix those venues while reporting
 * only one exchange to its market-data consumers, so the first eligible venue owns the
 * complete returned chain.
 */
export function selectCrossExchangeOptionChain(
  underlying: Instrument | null | undefined,
  backlinks: Map<string, Instrument[]>
): { instruments: Instrument[]; exchange: Exchange | undefined } {
  const options = derivativesOf(underlying, backlinks).filter(
    (instrument) =>
      instrument.isActive &&
      instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION &&
      instrument.exchange !== underlying?.exchange
  )
  const exchange = options[0]?.exchange
  return {
    exchange,
    instruments:
      exchange === undefined
        ? []
        : options.filter((instrument) => instrument.exchange === exchange),
  }
}
